import cn from 'classnames';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import selectCalendarCountriesOperation from 'editor/src/store/design/operation/calendarCountries/selectCalendarCountriesOperation';
import { useDispatch, useSelector } from 'editor/src/store/hooks';

import { getCountryOptions } from 'editor/src/util/calendar/calendarCountries';
import useTr from 'editor/src/util/useTr';

import Select, { SelectOption } from 'editor/src/component/Select';
import SpinnerLocal from 'editor/src/component/SpinnerLocal';

import styles from './index.module.scss';

const NO_COUNTRIES: string[] = [];

type Props = {
  disabled?: boolean;
};

function HolidayCountries({ disabled }: Props) {
  const tr = useTr();
  const { i18n } = useTranslation();
  const dispatch = useDispatch();
  const supportedCountries = useSelector((state) => state.hostSettings.calendarEventsCountries);
  const selectedCountries = useSelector((state) => state.design.designData?.calendar?.countries) ?? NO_COUNTRIES;
  const isLoading = useSelector((state) => !!state.gridDesigns.requestedCalendarCountries);

  const options = useMemo(
    () => getCountryOptions(supportedCountries, i18n.languageWithDash ?? 'en'),
    [supportedCountries, i18n.languageWithDash],
  );

  const onSelect = (option: SelectOption) => {
    const country = option.value as string;
    const countries = selectedCountries.includes(country)
      ? selectedCountries.filter((selected) => selected !== country)
      : [...selectedCountries, country];
    dispatch(selectCalendarCountriesOperation(countries));
  };

  if (!supportedCountries.length) {
    return null;
  }

  return (
    <div className={cn(styles.holidayCountries, { [styles.disabled]: disabled })}>
      <Select
        className="cy-holiday-countries"
        label={tr('editor-calendar-holiday-countries', 'Countries')}
        placeholder={tr('editor-calendar-select-countries', 'Select countries')}
        options={options}
        selected={selectedCountries}
        onSelect={onSelect}
        isMultiSelect
      />
      <SpinnerLocal show={isLoading} className={styles.spinner} />
    </div>
  );
}

export default React.memo(HolidayCountries);
